import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  createCustomer,
  createCustomerTenancy,
  deleteCustomerTenancy,
  updateCustomer,
} from "../TenancyApi";
import { useEffect, useState, useContext } from "react";
import GuestForm from "./GuestForm";
import { updateRows } from "../CommonFunctions";
import AddReview from "../AddReview";
import CommonComponents from "../CommonComponents";
import GuestCheckinFlow from "./GuestCheckinFlow";

export default function GuestDetails({ tenancyId, customersDetails }) {
  // LOCAL UI STATE
  const [guestList, setGuestList] = useState([]);
  const [guestForm, setGuestForm] = useState({});
  const [showAddReview, setShowAddReview] = useState(false);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    guestId: null,
    guestName: "",
  });

  const { displayNotificationMessage } = useContext(CommonComponents);

  // Normalize incoming data so each guest always has an "id"
  useEffect(() => {
    if (customersDetails && customersDetails.length > 0) {
      const normalized = customersDetails.map((g) => ({
        ...g,
        id: g.customerId,
      }));
      setGuestList(normalized);
    } else {
      setGuestList([]);
    }
  }, [customersDetails]);

  // ADD GUEST — opens GuestCheckinFlow dialog
  function handleAddGuest() {
    setShowAddGuest(true);
  }

  // CHECKIN COMPLETE — called by GuestCheckinFlow after save
  function handleCheckinComplete({ action, guest }) {
    if (action === "created") {
      const newGuest = { ...guest, id: guest.customerId || guest.id };
      createCustomerTenancy(tenancyId, newGuest.id);
      setGuestList((prev) => updateRows(prev, "create", null, newGuest));
      const guestName = `${guest.fname || ""} ${guest.lname || ""}`.trim() || "Guest";
      displayNotificationMessage("success", `${guestName} details added`);
    }
    setShowAddGuest(false);
  }

  // EDIT GUEST — still uses existing GuestForm
  function handleEditGuest(guest) {
    setGuestForm({
      open: true,
      mode: "edit",
      id: guest.id,
      data: { ...guest },
    });
  }

  function handleClose() {
    setGuestForm((prev) => ({ ...prev, open: false }));
  }

  // SAVE HANDLER — edit only (add now handled by GuestCheckinFlow)
  function handleSave(form) {
    updateApiCustomer(form);
  }

  // UPDATE CUSTOMER
  const updateApiCustomer = async (form) => {
    try {
      const response = await updateCustomer(guestForm.id, form);

      if (response) {
        setGuestList((prev) =>
          updateRows(prev, "edit", guestForm.id, {
            ...form,
            id: guestForm.id,
          })
        );
        const guestName = `${form.fname || ""} ${form.lname || ""}`.trim() || "Guest";
        displayNotificationMessage("success", `${guestName} details have been updated`);
      }
    } catch (error) {
      console.log(error);
      displayNotificationMessage("error", "Failed to update guest. Please try again.");
    }
  };

  // OPEN DELETE CONFIRMATION DIALOG
  const handleDeleteClick = (guest) => {
    const guestName = `${guest.fname || ""} ${guest.lname || ""}`.trim() || "this guest";
    setDeleteConfirmDialog({
      open: true,
      guestId: guest.id,
      guestName: guestName,
    });
  };

  // CLOSE DELETE DIALOG
  const handleCancelDelete = () => {
    setDeleteConfirmDialog({
      open: false,
      guestId: null,
      guestName: "",
    });
  };

  // CONFIRM DELETE
  const handleConfirmDelete = async () => {
    try {
      await deleteCustomerTenancy(tenancyId, deleteConfirmDialog.guestId);

      setGuestList((prev) => updateRows(prev, "delete", deleteConfirmDialog.guestId));
      displayNotificationMessage("success", `${deleteConfirmDialog.guestName} removed from tenancy`);
      handleCancelDelete();
    } catch (error) {
      console.log(error);
      displayNotificationMessage("error", "Failed to remove guest. Please try again.");
    }
  };

  return (
    <>
      <Box sx={{ borderRadius: 1, overflow: "hidden", bgcolor: "white", px: 2 }}>
        <Box component={Paper} elevation={3}>
          {/* Header */}
          <Box
            sx={{
              bgcolor: "#e8f3ff",
              px: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: "18px",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 0.2 }}>
              Guest Details
            </Typography>

            <Box>
              <Link
                to="#"
                style={{ color: "#0066ff", fontSize: "0.85rem", fontWeight: 500, marginRight: 50 }}
                onClick={() => setShowAddReview(true)}
              >
                Add Review
              </Link>

              <Link
                to="#"
                style={{ color: "#0066ff", fontSize: "0.85rem", fontWeight: 500 }}
                onClick={handleAddGuest}
              >
                Add Customer
              </Link>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table
              sx={{
                "& th": {
                  fontWeight: 600,
                  bgcolor: "white",
                  borderBottom: "1px solid #e3e3e3",
                  paddingY: 1,
                  paddingX: 2,
                },
                "& td": {
                  borderBottom: "1px solid #efefef",
                  fontSize: "0.92rem",
                  paddingY: 1,
                  paddingX: 2,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Reviews</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {guestList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: "center", padding: "16px" }}>
                      No guests found for this tenancy.
                    </TableCell>
                  </TableRow>
                )}

                {guestList.map((guest) => (
                  <TableRow key={guest.id}>
                    <TableCell>
                      {guest.fname || guest.lname
                        ? `${guest.fname ?? ""} ${guest.lname ?? ""}`.trim()
                        : "—"}
                    </TableCell>
                    <TableCell>{guest.email || "—"}</TableCell>
                    <TableCell>{guest.phone || "—"}</TableCell>
                    <TableCell>
                      {guest.address
                        ? `${guest.address ?? ""} ${guest.address.state ?? ""}`
                        : "—"}
                    </TableCell>
                    <TableCell>{guest.reviewCount}</TableCell>
                    <TableCell>
                      <Link
                        to="#"
                        style={{ color: "#0066ff" }}
                        onClick={() => handleEditGuest(guest)}
                      >
                        Edit
                      </Link>
                      <span style={{ margin: "0 8px", color: "#aaa" }}>|</span>
                      <Link
                        to="#"
                        style={{ color: "#d32f2f" }}
                        onClick={() => handleDeleteClick(guest)}
                      >
                        Delete
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Add Guest — GuestCheckinFlow Dialog */}
      <Dialog
        open={showAddGuest}
        onClose={() => setShowAddGuest(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Customer</DialogTitle>
        <DialogContent>
          <GuestCheckinFlow
            onComplete={handleCheckinComplete}
            onCancel={() => setShowAddGuest(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Guest — existing GuestForm */}
      <GuestForm
        open={guestForm.open}
        mode={guestForm.mode}
        data={guestForm.data}
        onClose={handleClose}
        onSave={handleSave}
      />

      {/* Add Review Modal */}
      {showAddReview && (
        <AddReview
          showAddReview={showAddReview}
          setShowAddReview={setShowAddReview}
          tenancyId={tenancyId}
          customersDetails={guestList}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog.open} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body">
            Are you sure you want to remove <strong>{deleteConfirmDialog.guestName}</strong> from this tenancy?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}